import React, { Component } from 'react';
import { defaultsDeep, sumBy } from 'lodash';
import { inject, observer } from 'mobx-react';
import { MdAssessment } from 'react-icons/md';
import { Box, Flex, Heading, Icon, ProgressBar, Text } from 'core/components';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import { addCommas } from 'app/util/utils';
import { COMPANY_MPS_HISTORY_QUERY, COMPANY_MPS_QUERY } from 'app/views/metrics/queries/index.js';
import MetricsExplorerLink from 'app/views/metrics/MetricsExplorerLink.js';
import { getMaxMps } from 'app/util/plans';
import LabelValue from './LabelValue';
import PlanDevicesPopover from './PlanDevicesPopover';
import { PlanCardContainer, PlanCardLeftFlex, PlanCardMainContentWrapper, PlanIconBox } from './layout';
import FreeTrialCallout from './FreeTrialCallout';

const COMPANY_MPS_QUERY_TS = defaultsDeep({ kmetrics: { includeTimeseries: 10 } }, COMPANY_MPS_QUERY);

@inject('$metrics', '$setup')
@observer
class MetricsPlanCard extends Component {
  render() {
    const {
      id,
      name: planName,
      description: planDescription,
      device_count: planDeviceCount,
      max_devices,
      max_fps,
      free_credits,
      paid_credits,
      pak_fps,
      cloud_provider,
      fast_retention,
      full_retention,
      plan_type,
      trialDaysRemaining,
      usedSyntheticCredits,
      $metrics,
      $setup,
      ...rest
    } = this.props;

    const name = planName;
    const icon = <Icon icon={MdAssessment} iconSize={40} color="primary" />;
    const isFreeTrialPlan = planName === 'Free Trial Plan';
    // metricsPerSec includes all metrics for the company, which may include devices in flow plans
    // that also send metrics, so use $setup.nmsDeviceCount instead of planDevices.
    const device_count = $setup.nmsDeviceCount;

    return (
      <PlanCardContainer {...rest}>
        <PlanCardLeftFlex>
          <PlanIconBox>{icon}</PlanIconBox>
          <PlanCardMainContentWrapper>
            <Text color="muted" as="div" mb="4px">
              MetricsPak
            </Text>
            <Heading level={4} mb={1}>
              {name}{' '}
              <Text fontWeight={200} muted>
                ({id})
              </Text>
            </Heading>
            <Text color="muted" as="div" mb={1}>
              {planDescription}
            </Text>

            {device_count === 0 && <Text color="muted">No devices have been added to this plan.</Text>}

            {device_count > 0 && (
              <LightweightDataViewWrapper query={COMPANY_MPS_QUERY}>
                {({ loading, results }) => {
                  if (loading) {
                    return (
                      <Box mt="4px">
                        <Text color="muted">Loading...</Text>
                      </Box>
                    );
                  }

                  if (results?.models?.length > 0) {
                    let progressIntent = 'none';
                    let progressPercent = 0;
                    const planMaxMps = getMaxMps({ plan_type, pak_fps, max_fps });
                    const avgMps = sumBy(results.models, (res) => res.get('avg_metricsPerSec'));
                    const progressValue = !avgMps ? 1 : avgMps / planMaxMps;

                    if (avgMps) {
                      progressIntent = Math.ceil(avgMps) > planMaxMps ? 'danger' : 'primary';
                      progressPercent = planMaxMps === 0 ? 100 : progressValue * 100;
                    }

                    return (
                      <Box mt="4px">
                        <Box mb={1}>
                          <ProgressBar
                            value={progressValue}
                            animate={loading}
                            stripes={loading}
                            intent={progressIntent}
                          />
                        </Box>
                        <Flex justifyContent="space-between" alignItems="center">
                          <>
                            <Flex alignItems="center">
                              <div>
                                <Text fontWeight="bold">
                                  {avgMps ? addCommas(Math.ceil(avgMps)) : 0} metrics/second
                                </Text>{' '}
                                <MetricsExplorerLink query={COMPANY_MPS_QUERY_TS}>in the last hour</MetricsExplorerLink>
                                {' | '}
                                <MetricsExplorerLink query={COMPANY_MPS_HISTORY_QUERY}>
                                  30 day history
                                </MetricsExplorerLink>
                              </div>
                            </Flex>
                            <Text color="muted">{progressPercent.toFixed(2)}%</Text>
                          </>
                        </Flex>
                      </Box>
                    );
                  }
                  return <></>;
                }}
              </LightweightDataViewWrapper>
            )}

            {isFreeTrialPlan && <FreeTrialCallout trialDaysRemaining={trialDaysRemaining} />}
          </PlanCardMainContentWrapper>
        </PlanCardLeftFlex>

        <Box>
          <LabelValue label="Plan MPS" value={addCommas(max_fps)} />
          <LabelValue
            label="Devices"
            value={
              <>
                <Box as="span" pr="4px">
                  {device_count} of {max_devices}
                  &nbsp;
                  {planDeviceCount > 0 && <PlanDevicesPopover planId={id} />}
                </Box>
              </>
            }
          />
          <LabelValue
            label="ICMP-Only Devices"
            value={
              <>
                <Box as="span">{$setup.nmsIcmpOnlyDeviceCount}</Box>
              </>
            }
          />
          <LabelValue label="Data Retention" value={`${full_retention} days`} mb={0} />
        </Box>
      </PlanCardContainer>
    );
  }
}

export default MetricsPlanCard;
